import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid as MuiGrid,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import { Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon, Lock as LockIcon } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { userApi } from '../../api/auth';
import { UserUpdate, UserAdminUpdate } from '../../types/user';
import { authApi } from '../../api/auth';

// Define Grid component with proper React memo to prevent hooks issues
const Grid = React.memo(function MemorizedGrid(props: any) {
  return <MuiGrid {...props} />;
});

const UserProfile: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  
  const [formData, setFormData] = useState<UserUpdate & { ecwid_customer_id?: string }>({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    ecwid_customer_id: user?.ecwid_customer_id?.toString() || ''
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleEdit = () => {
    setFormData({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      email: user?.email || '',
      ecwid_customer_id: user?.ecwid_customer_id?.toString() || ''
    });
    setEditing(true);
    setError(null);
    setSuccess(false);
  };
  
  const handleCancel = () => {
    setEditing(false);
    setError(null);
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const updateData: UserUpdate = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email
      };
      
      // If user is admin, also handle ecwid_customer_id update
      if (user.role === 'admin') {
        const adminUpdateData: UserAdminUpdate = { ...updateData };
        
        if (formData.ecwid_customer_id === '') {
          // Clear ecwid_customer_id if field is empty
          adminUpdateData.ecwid_customer_id = undefined;
        } else if (formData.ecwid_customer_id) {
          const parsedId = parseInt(formData.ecwid_customer_id);
          if (isNaN(parsedId)) {
            setError('Ecwid Customer ID must be a valid number');
            setLoading(false);
            return;
          }
          adminUpdateData.ecwid_customer_id = parsedId;
        }
        
        await userApi.adminUpdateUser(user.id, adminUpdateData);
      } else {
        await userApi.updateUser(user.id, updateData);
      }
      
      await refreshUser();
      setSuccess(true);
      setEditing(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
  };

  const handleChangePasswordClick = () => {
    setPasswordDialogOpen(true);
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordError(null);
    setPasswordSuccess(false);
  };

  const handlePasswordDialogClose = () => {
    setPasswordDialogOpen(false);
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setPasswordError(null);
    setPasswordLoading(true);

    // Validation
    if (passwordData.newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters long');
      setPasswordLoading(false);
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('Passwords do not match');
      setPasswordLoading(false);
      return;
    }

    try {
      await authApi.changePassword(passwordData.currentPassword, passwordData.newPassword);
      setPasswordSuccess(true);
      // Clear form after success
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      // Close dialog after a short delay
      setTimeout(() => {
        setPasswordDialogOpen(false);
        setPasswordSuccess(false);
      }, 1500);
    } catch (err: any) {
      setPasswordError(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };
  
  if (!user) {
    return (
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Typography>Please log in to view your profile</Typography>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card sx={{ mt: 4 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" component="h2">
            User Profile
          </Typography>
          
          <Box>
            {!editing ? (
              <>
                <Button
                  startIcon={<EditIcon />}
                  onClick={handleEdit}
                  variant="outlined"
                  sx={{ mr: 1 }}
                >
                  Edit Profile
                </Button>
                <Button
                  startIcon={<LockIcon />}
                  onClick={handleChangePasswordClick}
                  variant="outlined"
                  color="secondary"
                >
                  Change Password
                </Button>
              </>
            ) : (
              <Box>
                <IconButton color="primary" onClick={handleSubmit} disabled={loading}>
                  <SaveIcon />
                </IconButton>
                <IconButton color="error" onClick={handleCancel} disabled={loading}>
                  <CancelIcon />
                </IconButton>
              </Box>
            )}
          </Box>
        </Box>
        
        <Divider sx={{ mb: 3 }} />
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Profile updated successfully!
          </Alert>
        )}
        
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        
        <Grid container spacing={2}>
          {editing ? (
            // Edit mode
            <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                {user.role === 'admin' && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Ecwid Customer ID"
                      name="ecwid_customer_id"
                      value={formData.ecwid_customer_id}
                      onChange={handleChange}
                      helperText="Leave blank to clear"
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
          ) : (
            // View mode
            <>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" color="text.secondary">
                  Name
                </Typography>
                <Typography variant="body1">
                  {user.first_name} {user.last_name}
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1">{user.email}</Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" color="text.secondary">
                  Role
                </Typography>
                <Chip 
                  label={user.role === 'admin' ? 'Administrator' : 'Client'} 
                  color={user.role === 'admin' ? 'primary' : 'default'} 
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" color="text.secondary">
                  Status
                </Typography>
                <Chip 
                  label={user.status.charAt(0).toUpperCase() + user.status.slice(1)} 
                  color={
                    user.status === 'approved' 
                      ? 'success' 
                      : user.status === 'pending' 
                        ? 'warning' 
                        : 'error'
                  } 
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" color="text.secondary">
                  Ecwid Customer ID
                </Typography>
                <Typography variant="body1">{user.ecwid_customer_id || 'Not linked'}</Typography>
              </Grid>
            </>
          )}
        </Grid>

        {/* Change Password Dialog */}
        <Dialog open={passwordDialogOpen} onClose={handlePasswordDialogClose}>
          <DialogTitle>Change Password</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Please enter your current password and a new password.
            </DialogContentText>
            
            {passwordError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {passwordError}
              </Alert>
            )}
            
            {passwordSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Password updated successfully!
              </Alert>
            )}
            
            <Box component="form" onSubmit={handlePasswordSubmit}>
              <TextField
                margin="dense"
                label="Current Password"
                name="currentPassword"
                type="password"
                fullWidth
                value={passwordData.currentPassword}
                onChange={handlePasswordChange}
                required
              />
              <TextField
                margin="dense"
                label="New Password"
                name="newPassword"
                type="password"
                fullWidth
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                required
                helperText="Password must be at least 8 characters long"
              />
              <TextField
                margin="dense"
                label="Confirm New Password"
                name="confirmPassword"
                type="password"
                fullWidth
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange}
                required
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handlePasswordDialogClose} color="primary">
              Cancel
            </Button>
            <Button 
              onClick={handlePasswordSubmit} 
              color="primary" 
              disabled={passwordLoading || passwordSuccess}
              startIcon={passwordLoading ? <CircularProgress size={20} /> : null}
            >
              Change Password
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default UserProfile; 